import { getApiBaseUrl } from '@/config/api';
import {
  pollAirwallexPayment,
  toCreditCardPaymentMessage,
} from '../plugins/airwallex-payment';
import {
  AIRWALLEX_PAYMENT_METHOD_ID,
  isLockPreservingAirwallexFailure,
  resolveAirwallexPaymentIntentId,
} from './airwallex-recovery';

const AIRWALLEX_CART_RECOVERY_VERSION = 1;
const AIRWALLEX_CART_RECOVERY_ACKNOWLEDGE_TIMEOUT_MS = 3000;
const AIRWALLEX_CART_FAILURE_REFRESH_DELAYS_MS = [0, 300, 700, 1500];
const AIRWALLEX_CART_RECOVERY_ACTIONS = [
  'authorize',
  'check_status',
  'finalize',
  'completed',
  'blocked',
];
function normalizedIdentifier(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();

  return normalizedValue === '' ? null : normalizedValue;
}

function normalizedSupportReference(value) {
  const reference = normalizedIdentifier(value);

  return reference
    && /^CC1-(SGD|USD|AUD)-[0-9A-Z]+-[0-9A-F]{6}$/.test(reference)
    ? reference
    : null;
}

export default {
  data() {
    return {
      airwallexPaymentRecovery: null,
      airwallexCartRecoveryLoading: true,
      airwallexCartCancellationInProgress: false,
      airwallexCartCompletionInProgress: false,
      airwallexCartRecoveryUnavailable: false,
      airwallexCartRecoveryRequestEpoch: 0,
      airwallexCartRecoveryDestroyed: false,
    };
  },
  beforeDestroy() {
    this.airwallexCartRecoveryDestroyed = true;
    this.airwallexCartRecoveryRequestEpoch += 1;
    this.airwallexCartRecoveryLoading = false;
    this.airwallexCartCancellationInProgress = false;
  },
  computed: {
    hasAirwallexCartPayment() {
      return !!this.airwallexPaymentRecovery;
    },
  },
  methods: {
    airwallexCartRecoveryEndpoint() {
      return 'partner/shopping-cart';
    },
    airwallexCartRecoveryScope() {
      return 'regular';
    },
    fetchAirwallexCartRecovery() {
      return this.$api.get(this.airwallexCartRecoveryEndpoint(), {
        suppressErrorAlert: true,
      });
    },
    airwallexCartCancellationEndpoint() {
      return 'partner/payment/cart-recovery/cancel';
    },
    syncAirwallexCartData() {
    },
    pollAirwallexCartPayment(sendRequest, initialResponse = null) {
      return pollAirwallexPayment(
        sendRequest,
        initialResponse,
        undefined,
        () => !this.airwallexCartRecoveryDestroyed,
      );
    },
    onAirwallexCartRecoveryApplied() {
    },
    onAirwallexCartRecoveryCompleted() {
      throw new Error('The cart view must handle a completed credit card payment recovery.');
    },
    isCurrentAirwallexCartRecoveryRequest(requestEpoch) {
      return !this.airwallexCartRecoveryDestroyed
        && requestEpoch === this.airwallexCartRecoveryRequestEpoch;
    },
    normalizeAirwallexCartRecovery(recovery) {
      if (recovery === null) {
        return null;
      }

      if (!recovery
        || typeof recovery !== 'object'
        || Array.isArray(recovery)
      ) {
        throw new Error(
          'The credit card payment recovery status is invalid.',
        );
      }

      const version = Number(recovery.version);
      const validAction = AIRWALLEX_CART_RECOVERY_ACTIONS.includes(recovery.action);
      const expectedScope = this.airwallexCartRecoveryScope();
      const validScope = recovery.scope === expectedScope;
      const action = version === AIRWALLEX_CART_RECOVERY_VERSION
        && validAction
        && validScope
        ? recovery.action
        : 'blocked';
      const recoverable = action !== 'blocked' && recovery.recoverable === true;
      const mode = recoverable && ['new', 'saved'].includes(recovery.mode)
        ? recovery.mode
        : null;
      const paymentCardId = mode === 'saved'
        && Number.isFinite(Number(recovery.payment_card_id))
        && Number(recovery.payment_card_id) > 0
        ? Number(recovery.payment_card_id)
        : null;
      const hasPaymentStatus = recovery.payment_status_id !== null
        && typeof recovery.payment_status_id !== 'undefined'
        && String(recovery.payment_status_id).trim() !== '';
      const paymentStatusId = hasPaymentStatus
        && Number.isFinite(Number(recovery.payment_status_id))
        ? Number(recovery.payment_status_id)
        : null;
      const canSwitchPaymentMethod = recoverable
        && action === 'authorize'
        && !!normalizedIdentifier(recovery.payment_intent_id)
        && recovery.can_switch_payment_method === true;

      return {
        version: AIRWALLEX_CART_RECOVERY_VERSION,
        payment_type_id: Number(recovery.payment_type_id) === 8 ? 8 : AIRWALLEX_PAYMENT_METHOD_ID,
        attempt_id: normalizedIdentifier(recovery.attempt_id),
        scope: expectedScope,
        action,
        recoverable,
        payment_intent_id: normalizedIdentifier(recovery.payment_intent_id),
        mode,
        payment_card_id: paymentCardId,
        amount: recovery.amount === null || typeof recovery.amount === 'undefined'
          ? null
          : String(recovery.amount),
        payment_status_id: paymentStatusId,
        invoice: recovery.invoice && typeof recovery.invoice === 'object'
          ? recovery.invoice
          : null,
        reason_code: normalizedIdentifier(recovery.reason_code),
        can_switch_payment_method: canSwitchPaymentMethod,
        support_reference: normalizedSupportReference(
          recovery.support_reference,
        ),
      };
    },
    applyAirwallexCartRecovery(recovery) {
      if (this.airwallexCartRecoveryDestroyed) {
        return this.airwallexPaymentRecovery;
      }

      const normalizedRecovery = this.normalizeAirwallexCartRecovery(recovery);

      this.airwallexCartRecoveryUnavailable = false;
      this.airwallexPaymentRecovery = normalizedRecovery;

      if (normalizedRecovery) {
        this.paymentMethod = normalizedRecovery.payment_type_id;
        this.onAirwallexCartRecoveryApplied(normalizedRecovery);
      }

      return normalizedRecovery;
    },
    markAirwallexCartRecoveryUnavailable() {
      const currentRecovery = this.airwallexPaymentRecovery;

      if (!currentRecovery) {
        this.airwallexCartRecoveryUnavailable = true;
        return null;
      }

      const unavailableRecovery = this.applyAirwallexCartRecovery({
        version: AIRWALLEX_CART_RECOVERY_VERSION,
        attempt_id: currentRecovery.attempt_id || null,
        scope: this.airwallexCartRecoveryScope(),
        action: 'blocked',
        recoverable: false,
        payment_intent_id: currentRecovery.payment_intent_id || null,
        mode: null,
        payment_card_id: null,
        amount: currentRecovery.amount || null,
        payment_status_id: currentRecovery.payment_status_id || null,
        invoice: currentRecovery.invoice || null,
        reason_code: currentRecovery.reason_code || null,
        can_switch_payment_method: false,
        support_reference: currentRecovery.support_reference || null,
      });

      this.airwallexCartRecoveryUnavailable = true;
      return unavailableRecovery;
    },
    rememberAirwallexCartRecovery(overrides = {}) {
      const currentRecovery = this.airwallexPaymentRecovery || {};

      return this.applyAirwallexCartRecovery({
        version: AIRWALLEX_CART_RECOVERY_VERSION,
        payment_type_id: currentRecovery.payment_type_id || Number(this.paymentMethod) || 7,
        attempt_id: currentRecovery.attempt_id || null,
        scope: this.airwallexCartRecoveryScope(),
        action: currentRecovery.action || 'authorize',
        recoverable: currentRecovery.recoverable !== false,
        payment_intent_id: currentRecovery.payment_intent_id || null,
        mode: currentRecovery.mode || null,
        payment_card_id: currentRecovery.payment_card_id || null,
        amount: currentRecovery.amount || null,
        payment_status_id: currentRecovery.payment_status_id || null,
        invoice: currentRecovery.invoice || null,
        reason_code: currentRecovery.reason_code || null,
        can_switch_payment_method: currentRecovery.can_switch_payment_method === true,
        support_reference: currentRecovery.support_reference || null,
        ...overrides,
      });
    },
    syncAirwallexCartRecoveryResponse(cart, preserveWhenMissing = false) {
      // Contract: regular-cart reads and every mutation whose response reaches
      // this method (shopping-cart get/delete/coupon endpoints and
      // product/register) must include airwallexPaymentRecovery, using explicit
      // null when no server-side recovery exists.
      if (this.airwallexCartRecoveryDestroyed) {
        return;
      }

      const currentRecovery = this.airwallexPaymentRecovery;

      try {
        if (!cart
          || typeof cart !== 'object'
          || Array.isArray(cart)
          || !Object.prototype.hasOwnProperty.call(
            cart,
            'airwallexPaymentRecovery',
          )
        ) {
          throw new Error(
            preserveWhenMissing
              ? 'The credit card payment recovery status is unavailable.'
              : 'The credit card payment recovery status is missing.',
          );
        }

        const recovery = this.normalizeAirwallexCartRecovery(
          cart.airwallexPaymentRecovery,
        );

        this.syncAirwallexCartData(cart);
        this.applyAirwallexCartRecovery(recovery);
      } catch (error) {
        this.airwallexPaymentRecovery = currentRecovery;
        this.airwallexCartRecoveryUnavailable = true;
        throw error;
      }
    },
    refreshAirwallexCartRecovery(preserveWhenMissing = false) {
      const requestEpoch = this.airwallexCartRecoveryRequestEpoch + 1;

      this.airwallexCartRecoveryRequestEpoch = requestEpoch;
      this.airwallexCartRecoveryLoading = true;

      return Promise.resolve()
        .then(() => this.fetchAirwallexCartRecovery())
        .then((response) => {
          if (this.isCurrentAirwallexCartRecoveryRequest(requestEpoch)) {
            if (!response
              || !response.data
              || typeof response.data !== 'object'
              || !Object.prototype.hasOwnProperty.call(
                response.data,
                'airwallexPaymentRecovery',
              )
            ) {
              throw new Error(
                'The credit card payment status response is incomplete.',
              );
            }

            this.syncAirwallexCartRecoveryResponse(
              response.data,
              preserveWhenMissing,
            );
            this.airwallexCartRecoveryUnavailable = false;
          }

          return response;
        })
        .catch((error) => {
          if (this.isCurrentAirwallexCartRecoveryRequest(requestEpoch)) {
            if (this.airwallexPaymentRecovery) {
              this.markAirwallexCartRecoveryUnavailable();
            } else {
              this.airwallexCartRecoveryUnavailable = true;
            }
          }

          throw error;
        })
        .finally(() => {
          if (this.isCurrentAirwallexCartRecoveryRequest(requestEpoch)) {
            this.airwallexCartRecoveryLoading = false;
          }
        });
    },
    applyAirwallexCartRecoveryFromResponse(response) {
      if (this.airwallexCartRecoveryDestroyed) {
        return this.airwallexPaymentRecovery;
      }

      if (response
        && response.data
        && Object.prototype.hasOwnProperty.call(
          response.data,
          'airwallexPaymentRecovery',
        )
      ) {
        return this.applyAirwallexCartRecovery(
          response.data.airwallexPaymentRecovery,
        );
      }

      return this.airwallexPaymentRecovery;
    },
    hasAirwallexCartRecoveryPayload(response) {
      return !!(response
        && response.data
        && Object.prototype.hasOwnProperty.call(
          response.data,
          'airwallexPaymentRecovery',
        ));
    },
    resolveAirwallexCartPaymentIntentId(response) {
      const responseData = response && response.data
        && typeof response.data === 'object'
        ? response.data
        : {};
      const recovery = this.hasAirwallexCartRecoveryPayload(response)
        && responseData.airwallexPaymentRecovery
        && typeof responseData.airwallexPaymentRecovery === 'object'
        ? responseData.airwallexPaymentRecovery
        : null;
      const fallbackRecovery = this.airwallexPaymentRecovery;

      return resolveAirwallexPaymentIntentId(
        responseData.payment_intent_id,
        recovery && recovery.payment_intent_id,
        fallbackRecovery && fallbackRecovery.payment_intent_id,
      );
    },
    airwallexCartRecoveryAcknowledgeConfig(config = {}) {
      return {
        ...config,
        timeout: AIRWALLEX_CART_RECOVERY_ACKNOWLEDGE_TIMEOUT_MS,
        suppressErrorAlert: true,
      };
    },
    acknowledgeAirwallexCartRecovery(recovery) {
      if (!recovery || !recovery.attempt_id) {
        return Promise.resolve();
      }

      return this.$api.post('partner/payment/cart-recovery/acknowledge', {
        attempt_id: recovery.attempt_id,
      }, this.airwallexCartRecoveryAcknowledgeConfig());
    },
    async cancelAirwallexCartRecoveryForPaymentSwitch(
      recovery = this.airwallexPaymentRecovery,
      showError = true,
    ) {
      if (!recovery
        || !recovery.attempt_id
        || !recovery.payment_intent_id
        || recovery.recoverable !== true
        || recovery.action !== 'authorize'
        || this.airwallexCartCancellationInProgress
      ) {
        return false;
      }

      this.airwallexCartCancellationInProgress = true;
      // Invalidate an older cart refresh so it cannot re-apply the recovery
      // snapshot after the server has confirmed cancellation.
      this.airwallexCartRecoveryRequestEpoch += 1;

      try {
        const response = await this.$api.post(
          this.airwallexCartCancellationEndpoint(),
          {
            attempt_id: recovery.attempt_id,
          },
          {
            suppressErrorAlert: true,
          },
        );

        if (!response
          || !response.data
          || response.data.success !== true
        ) {
          throw new Error(
            'The credit card payment cancellation response is incomplete.',
          );
        }

        this.applyAirwallexCartRecovery(null);
        return true;
      } catch (error) {
        try {
          await this.refreshAirwallexCartRecovery(true);
        } catch (refreshError) {
          // A failed canonical refresh must retain the recovery lock.
        }

        if (showError) {
          this.$store.dispatch('addAlert', {
            id: Date.now(),
            type: 'error',
            message: this.airwallexCartErrorMessage(error),
          });
        }
        return false;
      } finally {
        this.airwallexCartCancellationInProgress = false;
      }
    },
    airwallexCartInvoiceUrl(invoice) {
      if (invoice && invoice.invoice_no && invoice.view_token) {
        const apiUrl = String(getApiBaseUrl() || '').replace(/\/$/, '');
        const invoiceNo = encodeURIComponent(String(invoice.invoice_no));
        const viewToken = encodeURIComponent(String(invoice.view_token));
        const systemCurrency = encodeURIComponent(
          String(window.systemCurrency || ''),
        );

        return `${apiUrl}/v1/tax-invoice/${invoiceNo}/view`
          + `?view_token=${viewToken}&system_currency=${systemCurrency}`;
      }

      if (!invoice || !invoice.invoice_id || !this.$router) {
        return null;
      }

      try {
        return this.$router.resolve({
          name: 'Retail.TaxInvoice',
          params: {
            brand: this.$store.state.brand,
            id: invoice.invoice_id,
            first: this.firstInvoice,
          },
        }).href;
      } catch (error) {
        return null;
      }
    },
    notifyAirwallexCartCompletionNavigationFailure(invoice) {
      const invoiceUrl = this.airwallexCartInvoiceUrl(invoice);
      const alert = {
        id: Date.now(),
        type: 'success',
        message: 'Your payment is complete, but the invoice page could not be opened.',
      };

      if (invoiceUrl) {
        alert.message += ' ';
        alert.action = {
          href: invoiceUrl,
          label: 'View invoice',
        };
      }

      this.$store.dispatch('addAlert', alert);
    },
    async completeAirwallexCartRecovery(recovery = this.airwallexPaymentRecovery) {
      if (this.airwallexCartRecoveryDestroyed) {
        return false;
      }

      if (this.airwallexCartCompletionInProgress) {
        return true;
      }

      const invoice = recovery && recovery.invoice;

      if (!invoice || !invoice.invoice_id) {
        this.rememberAirwallexCartRecovery({
          action: 'blocked',
          recoverable: false,
          invoice: null,
        });
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'error',
          message: 'The completed credit card payment is missing its invoice. '
            + `Please contact support.${
              this.airwallexCartSupportReferenceSuffix(recovery)
            }`,
        });
        return false;
      }

      this.airwallexCartCompletionInProgress = true;
      try {
        await this.acknowledgeAirwallexCartRecovery(recovery);
      } catch (error) {
        // A failed acknowledgement must not strand the customer on checkout.
        // The durable completed recovery remains available for a later retry.
      }

      if (this.airwallexCartRecoveryDestroyed) {
        this.airwallexCartCompletionInProgress = false;
        return false;
      }

      this.airwallexPaymentRecovery = null;
      let completionSucceeded = false;

      try {
        await this.onAirwallexCartRecoveryCompleted(invoice);
        completionSucceeded = true;
        return true;
      } catch (error) {
        if (!this.airwallexCartRecoveryDestroyed) {
          this.notifyAirwallexCartCompletionNavigationFailure(invoice);
        }
        return false;
      } finally {
        if (!completionSucceeded) {
          this.airwallexCartCompletionInProgress = false;
        }
      }
    },
    completeAirwallexCartInvoice(invoice) {
      const currentRecovery = this.airwallexPaymentRecovery || {};

      return this.completeAirwallexCartRecovery({
        ...currentRecovery,
        version: AIRWALLEX_CART_RECOVERY_VERSION,
        scope: this.airwallexCartRecoveryScope(),
        action: 'completed',
        recoverable: true,
        invoice,
      });
    },
    isLockPreservingAirwallexCartFailure(error) {
      return isLockPreservingAirwallexFailure(error);
    },
    waitForAirwallexCartFailureRefresh(delayMs) {
      if (delayMs <= 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    },
    async refreshAirwallexCartRecoveryAfterFailure(
      preserveRecoveryWhenMissing,
    ) {
      let lastResponse = null;
      let lastError = null;

      for (let index = 0;
        index < AIRWALLEX_CART_FAILURE_REFRESH_DELAYS_MS.length;
        index += 1
      ) {
        if (this.airwallexCartRecoveryDestroyed) {
          return null;
        }

        // Sequential waits are required so each read observes the state from
        // the preceding Airwallex transition.
        // eslint-disable-next-line no-await-in-loop
        await this.waitForAirwallexCartFailureRefresh(
          AIRWALLEX_CART_FAILURE_REFRESH_DELAYS_MS[index],
        );

        if (this.airwallexCartRecoveryDestroyed) {
          return null;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          lastResponse = await this.refreshAirwallexCartRecovery(
            preserveRecoveryWhenMissing,
          );
          lastError = null;

          if (this.airwallexCartRecoveryDestroyed) {
            return null;
          }

          const recovery = this.airwallexPaymentRecovery;

          if (!recovery || recovery.action !== 'check_status') {
            return lastResponse;
          }
        } catch (error) {
          lastError = error;
        }
      }

      if (lastError) {
        throw lastError;
      }

      return lastResponse;
    },
    airwallexCartSupportReferenceSuffix(
      recovery = this.airwallexPaymentRecovery,
    ) {
      const reference = recovery && recovery.support_reference;

      return reference ? ` Reference: ${reference}.` : '';
    },
    airwallexCartErrorMessage(error) {
      const responseData = error && error.response
        ? error.response.data
        : null;
      const message = responseData && typeof responseData === 'object'
        ? responseData.message
        : responseData;

      if (typeof message === 'string' && message) {
        return toCreditCardPaymentMessage(message);
      }

      return error && error.message
        ? toCreditCardPaymentMessage(error.message)
        : 'The credit card payment could not be completed.';
    },
    async handleAirwallexCartError(error) {
      const preserveRecoveryWhenMissing = this
        .isLockPreservingAirwallexCartFailure(error);
      let refreshResponse = null;

      try {
        refreshResponse = await this.refreshAirwallexCartRecoveryAfterFailure(
          preserveRecoveryWhenMissing,
        );
      } catch (refreshError) {
        // The original payment error remains the customer-facing error.
      }

      const refreshedRecovery = refreshResponse
        ? this.airwallexPaymentRecovery
        : null;

      if (refreshedRecovery && refreshedRecovery.action === 'completed') {
        await this.completeAirwallexCartRecovery(refreshedRecovery);
        return true;
      }

      if (refreshedRecovery
        && !this.airwallexCartRecoveryDestroyed
        && refreshedRecovery.action === 'authorize'
        && refreshedRecovery.reason_code === 'payment_method_required'
        && refreshedRecovery.recoverable === true
        && refreshedRecovery.attempt_id
        && refreshedRecovery.payment_intent_id
      ) {
        const released = await this.cancelAirwallexCartRecoveryForPaymentSwitch(
          refreshedRecovery,
          false,
        );

        if (!released
          && this.airwallexPaymentRecovery
          && this.airwallexPaymentRecovery.action === 'completed'
        ) {
          await this.completeAirwallexCartRecovery(
            this.airwallexPaymentRecovery,
          );
          return true;
        }
      }

      this.resetAirwallexCartRetryConsent();
      this.$store.dispatch('addAlert', {
        id: Date.now(),
        type: 'error',
        message: this.airwallexCartErrorMessage(error),
      });
      this.applyAirwallexCartRecovery(null);
      return false;
    },
    resetAirwallexCartRetryConsent() {
      if (this.$store.state.brand === 'cldy') {
        if (typeof this.tosAccepted !== 'undefined') {
          this.tosAccepted = false;
        }

        if (typeof this.tosAuditSucceeded !== 'undefined') {
          this.tosAuditSucceeded = false;
        }
      }
    },
  },
};
